import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const VIEWPORT_PADDING = 8;

function positionMenu(trigger, menu) {
    const rect = trigger.getBoundingClientRect();
    const width = Math.max(rect.width, 180);
    const height = menu?.offsetHeight ?? 0;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeAbove = height > 0 && spaceBelow < height + VIEWPORT_PADDING && rect.top > height + VIEWPORT_PADDING;
    const top = placeAbove ? rect.top - height - 4 : rect.bottom + 4;
    const maxLeft = window.innerWidth - width - VIEWPORT_PADDING;
    const left = Math.max(VIEWPORT_PADDING, Math.min(rect.left, maxLeft));
    return { top, left, width };
}

export default function ProjectAccessSelect({
    projects,
    selectedIds,
    lockedIds,
    disabled = false,
    onToggle
}) {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 180 });
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    const selected = projects.filter(project => selectedIds.includes(project.id));
    const label = selected.length === 0
        ? 'None'
        : selected.length === 1
            ? selected[0].name
            : `${selected.length} projects`;
    const title = selected.length === 0 ? 'No projects' : selected.map(project => project.name).join(', ');

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        setCoords(positionMenu(trigger, menuRef.current));
    }, []);

    useLayoutEffect(() => {
        if (!open) return undefined;
        updatePosition();
        return undefined;
    }, [open, updatePosition, projects]);

    useEffect(() => {
        if (!open) return undefined;
        const handlePointer = (event) => {
            const target = event.target;
            if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
            setOpen(false);
        };
        const handleKey = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };
        const handleScroll = () => setOpen(false);
        document.addEventListener('mousedown', handlePointer);
        document.addEventListener('keydown', handleKey);
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            document.removeEventListener('mousedown', handlePointer);
            document.removeEventListener('keydown', handleKey);
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [open, updatePosition]);

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className="input-field"
                disabled={disabled}
                title={title}
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => {
                    if (disabled) return;
                    setOpen(current => !current);
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.5rem',
                    minWidth: '8rem',
                    textAlign: 'left',
                    cursor: disabled ? 'not-allowed' : 'pointer'
                }}
            >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                <i className={`fa-solid ${open ? 'fa-caret-up' : 'fa-caret-down'}`} style={{ fontSize: '0.7em', color: 'var(--text-secondary)' }} />
            </button>
            {open && createPortal(
                <div
                    ref={menuRef}
                    role="listbox"
                    aria-multiselectable="true"
                    style={{
                        position: 'fixed',
                        top: coords.top,
                        left: coords.left,
                        width: coords.width,
                        zIndex: 80,
                        maxHeight: '240px',
                        overflowY: 'auto',
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
                        padding: '0.25rem'
                    }}
                >
                    {projects.map(project => {
                        const locked = lockedIds.includes(project.id);
                        const checked = locked || selectedIds.includes(project.id);
                        return (
                            <label
                                key={project.id}
                                title={locked ? 'This member created the project.' : 'Grant access to this project. Tool setup decides view or edit.'}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.4rem 0.5rem',
                                    fontSize: '0.85rem',
                                    cursor: locked ? 'not-allowed' : 'pointer',
                                    opacity: locked ? 0.7 : 1
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={locked}
                                    onChange={e => onToggle(project, e.target.checked)}
                                />
                                <span>{project.name}</span>
                            </label>
                        );
                    })}
                </div>,
                document.body
            )}
        </>
    );
}
